import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

// Draggable Product Component
export const DraggableProduct = ({ type, amount, emoji, isDragging }) => {
  const {attributes, listeners, setNodeRef} = useDraggable({
    id: type,
    data: { type: 'PRODUCT', productType: type },
    disabled: amount <= 0
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`
        select-none 
        p-2 rounded border border-stone-200
        transition-colors touch-none
        min-h-14 min-w-14
        ${amount > 0 
          ? 'cursor-grab hover:bg-stone-50 active:cursor-grabbing active:scale-95' + (isDragging ? ' opacity-0' : '')
          : 'cursor-not-allowed opacity-30'
        }
      `}
    >
      <div className="flex items-center justify-center h-full gap-2">
        <span className="text-3xl">{emoji}</span>
      </div>
    </div>
  );
};

// Drag Preview Component
export const DragPreview = ({ emoji }) => {
  return (
    <div className="bg-white shadow-lg rounded p-2 cursor-grabbing transform-gpu min-h-14 min-w-14 flex items-center justify-center">
      <span className="text-3xl">{emoji}</span>
    </div>
  );
};

// Droppable Customer Component
export const DroppableCustomer = ({ customer, onServe, productData, currentStock, earned, ratingLost }) => {
  const { t } = useTranslation();
  const {setNodeRef, isOver, active} = useDroppable({
    id: customer.id,
    data: { 
      wants: customer.wants,
      currentStock
    }
  });

  const isValidDrop = active ? 
    active.data.current?.productType === customer.wants && 
    currentStock[active.data.current?.productType]?.amount > 0 : 
    false;

  const content = (() => {
    if (customer.patience < 0) {  
      return (
        <div className="flex items-center gap-1 bg-red-100 rounded p-1 sm:p-2 w-14 sm:w-16 h-[36px] sm:h-[40px] justify-center shrink-0">
          <div className="text-xs sm:text-sm text-red-600">-0.5</div><Star size={12} className="fill-red-100 text-red-600" />
        </div>
      );
    }

    if (earned) { 
      return (
        <div className="flex items-center gap-1 bg-green-100 rounded p-1 sm:p-2 w-14 sm:w-16 h-[36px] sm:h-[40px] justify-center shrink-0">
          <span>{productData[customer.wants].e}</span>
          <div className="text-xs sm:text-sm text-green-600">{t('game.earned', { earned })}</div>
        </div>
      );
    }

    return (
      <div
        ref={setNodeRef}
        className={`
          flex items-center gap-1 rounded p-1 sm:p-2 w-14 sm:w-16 h-[36px] sm:h-[40px] justify-center shrink-0
          transition-colors duration-200
          ${isOver ? (isValidDrop ? 'bg-emerald-50 scale-105' : 'bg-rose-50 scale-105') : 
            customer.patience <= 3 ? 'bg-orange-200' : 
            customer.patience <= 6 ? 'bg-orange-100' : 
            'bg-stone-50'
          }
        `}
      >
        <span>{productData[customer.wants].e}</span>
        <div className="text-xs sm:text-sm">{customer.patience}s</div>
      </div>
    );
  })();

  return content;
};